import { GitCompare, Loader } from 'lucide-react'

import { Button } from '@krp/ui-kit'

import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import { WarehouseSelect } from '../../structure/ui/WarehouseSelect'
import type { AllocationActions, AllocationVm } from '../model/viewModels'

const DRAFT_TEXT = {
  pending: 'Изменено, сохранится через полсекунды',
  saving: 'Сохраняем…',
  failed: 'Не сохранено',
  unknown: 'Исход сохранения неизвестен',
  conflict: 'Строку изменили на сервере',
} as const

/**
 * Раздел «Распределение»: склады — ТОЛЬКО этой базы (прочитаны из 1С на лету),
 * ячейка — количество строки на складе. Набранное уходит debounce-listener-ом
 * с версией строки; конфликт показывает серверное значение рядом.
 */
export function AllocationSection({ vm, actions }: { vm: AllocationVm; actions: AllocationActions }) {
  return (
    <div className="onec-screen">
      <div className="onec-row">
        <WarehouseSelect
          label="Склад для заполнения"
          value={vm.fillWarehouseKey}
          pick={vm.warehousePick}
          placeholder="Выберите склад"
          onChange={actions.fillWarehouseChanged}
          onRetry={actions.reloadWarehouses}
        />
        <Button variant="ghost" onClick={actions.reload}>
          Перечитать
        </Button>
      </div>
      {vm.editable && (
        <div className="onec-actions">
          <Button variant="secondary" onClick={actions.allToOne} disabled={vm.fillWarehouseKey === ''}>
            Всё на выбранный склад
          </Button>
          <Button variant="secondary" onClick={actions.setDefaultWarehouse} disabled={vm.fillWarehouseKey === ''}>
            Сделать складом по умолчанию
          </Button>
          <Button variant="secondary" onClick={actions.evenly}>
            Поровну по складам
          </Button>
          <Button variant="ghost" onClick={actions.clear}>
            Очистить распределение
          </Button>
        </div>
      )}
      <OneCQueryState query={vm.query} emptyText="Строк для распределения нет" onRetry={actions.reload} />
      {vm.rows.length > 0 && (
        <>
          <p>
            Не распределено всего: {vm.unallocatedTotal} · документов в 1С: {vm.documentsToCreate}
          </p>
          <div className="onec-scroll">
            <table className="onec-table">
              <thead>
                <tr>
                  <th>№</th>
                  <th>Наименование</th>
                  <th data-numeric="true">Количество</th>
                  {vm.columns.map((column) => (
                    <th key={column.key} data-numeric="true">
                      {column.name}
                      {column.isMain ? ' · основной' : ''}
                      {column.isDefault ? ' · по умолчанию' : ''}
                      <div className="onec-muted">
                        {column.organizationName} · {column.total}
                      </div>
                    </th>
                  ))}
                  <th data-numeric="true">Остаток</th>
                  <th>Состояние</th>
                </tr>
              </thead>
              <tbody>
                {vm.rows.map((row) => (
                  <tr key={row.lineId} className={row.ignored ? 'onec-row-ignored' : undefined}>
                    <td>{row.lineNumber}</td>
                    <td>{row.name}</td>
                    <td data-numeric="true">{row.quantity}</td>
                    {vm.columns.map((column) => {
                      const cell = row.cells[column.key]
                      return (
                        <td key={column.key} data-numeric="true">
                          <input
                            className="onec-cell-input"
                            aria-label={`${column.name}, строка ${row.lineNumber}`}
                            inputMode="decimal"
                            value={cell?.value ?? ''}
                            data-changed={cell?.changed ?? false}
                            disabled={!vm.editable || row.ignored}
                            onChange={(event) => actions.edit(row.lineId, column.key, event.target.value)}
                          />
                          {cell?.changed === true && <span className="onec-muted">на сервере {cell.server === '' ? '0' : cell.server}</span>}
                        </td>
                      )
                    })}
                    <td data-numeric="true">
                      {row.unallocated}
                      {row.balanced ? ' ✓' : ' · не всё'}
                    </td>
                    <td>
                      {row.draft !== null && (
                        <div className="onec-draft" data-state={row.draft.state} role={row.draft.state === 'conflict' || row.draft.state === 'failed' || row.draft.state === 'unknown' ? 'alert' : 'status'}>
                          {row.draft.state === 'saving' && <Loader size={14} aria-hidden="true" />}
                          {row.draft.state === 'conflict' && <GitCompare size={14} aria-hidden="true" />}
                          <span>{DRAFT_TEXT[row.draft.state]}</span>
                          {row.draft.message !== null && <span>{row.draft.message}</span>}
                          {(row.draft.state === 'failed' || row.draft.state === 'unknown') && (
                            <Button variant="secondary" onClick={() => actions.retry(row.lineId)}>
                              {row.draft.state === 'unknown' ? 'Проверить исход' : 'Повторить'}
                            </Button>
                          )}
                          {row.draft.state === 'conflict' && (
                            <Button variant="secondary" onClick={() => actions.keepMine(row.lineId)}>
                              Оставить моё
                            </Button>
                          )}
                          {(row.draft.state === 'conflict' || row.draft.state === 'failed' || row.draft.state === 'unknown') && (
                            <Button variant="ghost" onClick={() => actions.takeServer(row.lineId)}>
                              Взять с сервера
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <OneCQueryState query={vm.problemsQuery} emptyText="" onRetry={actions.reload} />
      {vm.problems.length > 0 && (
        <ul className="onec-list" aria-label="Проблемы распределения">
          {vm.problems.map((problem, index) => (
            <li key={`${problem.lineNumber}-${index}`}>
              <p className="onec-problem">
                Строка {problem.lineNumber}: {problem.message}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
